<template>
    <div class="quiz-player" :style="playerStyle">
        <div v-if="loading" class="quiz-state">
            <q-spinner size="48px" />
        </div>
        <div v-else-if="!quiz || !quiz.questions.length" class="quiz-state">
            <p>Ce quiz ne contient aucune question.</p>
        </div>

        <!-- Écran final -->
        <div v-else-if="finished" class="quiz-card quiz-end">
            <h3 class="quiz-title">{{ quiz.title }}</h3>
            <template v-if="isSurvey">
                <q-icon name="check_circle" size="72px" class="quiz-thanks-icon" />
                <p class="quiz-score-label">Merci d'avoir répondu !</p>
            </template>
            <template v-else>
                <div class="quiz-score-circle">
                    <q-circular-progress :value="quiz.questions.length ? 100 * score / scorableCount : 0" size="160px" :thickness="0.12"
                        track-color="grey-3" show-value class="quiz-circular">
                        <span class="quiz-score">{{ score }}<small>/{{ scorableCount }}</small></span>
                    </q-circular-progress>
                </div>
                <p class="quiz-score-label">{{ scoreLabel }}</p>
            </template>
            <q-btn square unelevated no-caps class="quiz-accent-btn quiz-big-btn" icon="replay"
                label="Recommencer" @click="restart" />
        </div>

        <!-- Question courante -->
        <div v-else class="quiz-card">
            <div class="quiz-progress">
                <span class="quiz-progress-text">Question {{ currentIndex + 1 }} <small>/ {{ quiz.questions.length
                }}</small></span>
                <q-linear-progress :value="(currentIndex + 1) / quiz.questions.length" size="8px" rounded
                    class="quiz-progress-bar" track-color="grey-3" />
            </div>

            <h4 class="quiz-question-text">{{ currentQuestion.text }}</h4>

            <div v-if="mediaUrl" class="quiz-media">
                <img v-if="mediaType === 'image'" :src="mediaUrl" />
                <audio v-else-if="mediaType === 'audio'" :src="mediaUrl" controls></audio>
            </div>

            <!-- Question ouverte : saisie libre -->
            <div v-if="currentQuestion.type === 'open'" class="quiz-open">
                <q-input v-model="openText" type="textarea" outlined autogrow :disable="answered"
                    label="Votre réponse" class="quiz-open-input" />
            </div>

            <!-- Choix / Vrai-Faux -->
            <div v-else class="quiz-answers" :class="{ 'quiz-answers--tf': currentQuestion.type === 'truefalse' }">
                <button v-for="(answer, ai) in currentQuestion.answers" :key="answer.id" class="quiz-answer-btn"
                    :class="answerClass(answer)" :disabled="answered" @click="selectAnswer(answer)">
                    <template v-if="currentQuestion.type === 'truefalse'">
                        <q-icon :name="ai === 0 ? 'check' : 'close'" size="34px" class="quiz-tf-icon" />
                        <span>{{ answer.text }}</span>
                    </template>
                    <template v-else>
                        <span class="quiz-answer-letter">{{ 'ABCD'[ai] }}</span>
                        <span class="quiz-answer-text">{{ answer.text }}</span>
                    </template>
                    <template v-if="!isSurvey">
                        <q-icon v-if="answered && answer.is_correct" name="check_circle" size="26px"
                            class="quiz-answer-mark" />
                        <q-icon v-else-if="answered && answer.id === selectedAnswerId" name="cancel" size="26px"
                            class="quiz-answer-mark" />
                    </template>
                </button>
            </div>

            <transition appear enter-active-class="animate__animated animate__fadeInUp">
                <div v-if="currentQuestion.type === 'open' && !answered" class="quiz-next">
                    <q-space />
                    <q-btn square unelevated no-caps class="quiz-accent-btn quiz-big-btn"
                        :label="isLastQuestion ? 'Terminer' : 'Valider'"
                        :icon-right="isLastQuestion ? 'flag' : 'arrow_forward'"
                        :disable="!openText.trim()" @click="submitOpen" />
                </div>
                <div v-else-if="answered" class="quiz-next">
                    <div v-if="!isSurvey" class="quiz-feedback" :class="lastCorrect ? 'quiz-feedback--good' : 'quiz-feedback--bad'">
                        <q-icon :name="lastCorrect ? 'celebration' : 'sentiment_dissatisfied'" size="26px" />
                        {{ lastCorrect ? 'Bonne réponse !' : 'Mauvaise réponse' }}
                    </div>
                    <q-space v-else />
                    <q-btn square unelevated no-caps class="quiz-accent-btn quiz-big-btn"
                        :label="isLastQuestion ? (isSurvey ? 'Terminer' : 'Voir le score') : 'Question suivante'"
                        :icon-right="isLastQuestion ? 'flag' : 'arrow_forward'" @click="next" />
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { ref, computed, watch, onMounted } from 'vue';

// uuid v4 léger, sans dépendance (respondent anonyme)
function uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.floor(Math.random() * 16);
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
function getRespondentUid() {
    try {
        let uid = localStorage.getItem('quiz_respondent_uid');
        if (!uid) { uid = uuidv4(); localStorage.setItem('quiz_respondent_uid', uid); }
        return uid;
    } catch (e) { return null; }
}

export default {
    name: 'QuizPlayer',
    props: { quizId: { required: true } },
    setup(props) {
        const loading = ref(true);
        const quiz = ref(null);
        const currentIndex = ref(0);
        const selectedAnswerId = ref(null);
        const answered = ref(false);
        const lastCorrect = ref(false);
        const score = ref(0);
        const finished = ref(false);
        const mediaUrl = ref(null);
        const bgUrl = ref(null);
        const openText = ref('');
        const items = ref([]);        // réponses accumulées
        const submitting = ref(false);

        const currentQuestion = computed(() => quiz.value.questions[currentIndex.value]);
        const isLastQuestion = computed(() => currentIndex.value === quiz.value.questions.length - 1);
        const isSurvey = computed(() => quiz.value && quiz.value.mode === 'survey');
        const scorableCount = computed(() => quiz.value
            ? quiz.value.questions.filter(q => q.type === 'choice' || q.type === 'truefalse').length : 0);
        const scoreLabel = computed(() => {
            const denom = scorableCount.value || 1;
            const ratio = score.value / denom;
            if (ratio === 1) return 'Sans faute, bravo !';
            if (ratio >= 0.5) return 'Bien joué !';
            return 'Vous ferez mieux la prochaine fois !';
        });

        // Fond (couleur OU image) + couleur d'accent configurés dans l'éditeur de quiz
        const playerStyle = computed(() => {
            const style = {};
            const q = quiz.value;
            if (q && q.accent_color) style['--quiz-accent'] = q.accent_color;
            if (bgUrl.value) {
                style.backgroundImage = `url(${bgUrl.value})`;
                style.backgroundSize = 'cover';
                style.backgroundPosition = 'center';
            } else if (q && q.bg_color) {
                style.backgroundColor = q.bg_color;
            }
            return style;
        });

        const mediaType = computed(() => {
            const name = currentQuestion.value && currentQuestion.value.resource_name;
            if (!name) return null;
            const ext = name.split('.').pop().toLowerCase();
            if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
            if (['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(ext)) return 'audio';
            return null;
        });

        const fetchQuiz = async () => {
            loading.value = true;
            try {
                const url = `${process.env.VUE_APP_BASEURL}/quiz/${props.quizId}`;
                const response = await axios.get(url);
                quiz.value = response.data;
                if (quiz.value.bg_image_name) loadBg(quiz.value.bg_image_name);
            } catch (error) {
                console.log(error);
            } finally {
                loading.value = false;
            }
        };

        const loadBg = async (name) => {
            try {
                const response = await axios.get(`${process.env.VUE_APP_BASEURL}/s3Resource/${name}`);
                bgUrl.value = response.data;
            } catch (error) { console.log(error); }
        };

        const loadMedia = async () => {
            mediaUrl.value = null;
            const q = quiz.value && quiz.value.questions[currentIndex.value];
            if (!q || !q.resource_name) return;
            try {
                const url = `${process.env.VUE_APP_BASEURL}/s3Resource/${q.resource_name}`;
                const response = await axios.get(url);
                mediaUrl.value = response.data;
            } catch (error) { console.log(error); }
        };

        const recordItem = (item) => {
            items.value = items.value.filter(i => i.question_id !== item.question_id);
            items.value.push(item);
        };

        const selectAnswer = (answer) => {
            if (answered.value) return;
            selectedAnswerId.value = answer.id;
            answered.value = true;
            lastCorrect.value = !!answer.is_correct;
            if (!isSurvey.value && answer.is_correct) score.value++;
            recordItem({ question_id: currentQuestion.value.id, answer_id: answer.id, answer_text: null });
        };

        const submitOpen = () => {
            if (answered.value || !openText.value.trim()) return;
            recordItem({ question_id: currentQuestion.value.id, answer_id: null, answer_text: openText.value.trim() });
            answered.value = true;
            // question ouverte : pas de feedback, on enchaîne directement
            next();
        };

        const answerClass = (answer) => {
            if (!answered.value) return '';
            if (isSurvey.value) return answer.id === selectedAnswerId.value ? 'quiz-answer-btn--chosen' : 'quiz-answer-btn--muted';
            if (answer.is_correct) return 'quiz-answer-btn--correct';
            if (answer.id === selectedAnswerId.value) return 'quiz-answer-btn--wrong';
            return 'quiz-answer-btn--muted';
        };

        const submitResponse = async () => {
            if (submitting.value) return;
            submitting.value = true;
            try {
                const url = `${process.env.VUE_APP_BASEURL}/quiz/${props.quizId}/response`;
                const res = await axios.post(url, { respondent: getRespondentUid(), items: items.value });
                if (res.data && typeof res.data.score === 'number') score.value = res.data.score;
            } catch (error) {
                console.log(error);
            } finally {
                submitting.value = false;
            }
        };

        const next = async () => {
            if (isLastQuestion.value) {
                await submitResponse();
                finished.value = true;
            } else {
                currentIndex.value++;
            }
            answered.value = false;
            selectedAnswerId.value = null;
            openText.value = '';
        };

        const restart = () => {
            currentIndex.value = 0;
            score.value = 0;
            finished.value = false;
            answered.value = false;
            selectedAnswerId.value = null;
            openText.value = '';
            items.value = [];
        };

        watch([currentIndex, quiz], loadMedia);
        onMounted(async () => { await fetchQuiz(); await loadMedia(); });

        return {
            loading, quiz, currentIndex, selectedAnswerId, answered, lastCorrect, score, finished,
            currentQuestion, isLastQuestion, isSurvey, scorableCount, scoreLabel, mediaType, mediaUrl, playerStyle,
            openText, selectAnswer, submitOpen, answerClass, next, restart,
        };
    },
};
</script>

<style scoped>
/* Tactile d'abord : grandes cibles (≥ 56px), gros textes, aucun survol requis */
.quiz-player {
    width: 100%;
    height: 100%;
    min-height: inherit;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: clamp(8px, 2vw, 32px);
    color: var(--quiz-accent, var(--q-secondary));
}

.quiz-state {
    padding: 48px;
    text-align: center;
    background: rgba(255, 255, 255, 0.95);
    font-size: 1.2rem;
    color: #333;
}

.quiz-card {
    width: min(94vw, 780px);
    background: rgba(255, 255, 255, 0.97);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
    border-top: 6px solid var(--quiz-accent, var(--q-secondary));
    padding: clamp(18px, 3vw, 36px);
    max-height: 92vh;
    overflow-y: auto;
    color: #222;
}

/* ---- progression ---- */
.quiz-progress {
    margin-bottom: 14px;
}

.quiz-progress-text {
    font-size: 1.05rem;
    font-weight: 700;
    color: #444;
}

.quiz-progress-text small {
    font-weight: 400;
    color: #888;
}

.quiz-progress-bar {
    margin-top: 6px;
    color: var(--quiz-accent, var(--q-secondary));
}

/* ---- question ---- */
.quiz-question-text {
    margin: 10px 0 18px;
    font-size: clamp(1.25rem, 2.6vw, 1.7rem);
    line-height: 1.35;
    font-weight: 600;
    color: #1a1a1a;
}

.quiz-media {
    text-align: center;
    margin-bottom: 18px;
}

.quiz-media img {
    max-width: 100%;
    max-height: 34vh;
    object-fit: contain;
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.18);
}

.quiz-media audio {
    width: 100%;
    height: 48px;
}

/* ---- réponses ---- */
.quiz-answers {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.quiz-answer-btn {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    min-height: 60px;
    padding: 12px 16px;
    border: 2px solid #e2e2e2;
    background: #f7f7f7;
    color: #222;
    font-size: 1.1rem;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    transition: background .12s ease, border-color .12s ease, transform .08s ease;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
}

.quiz-answer-btn:not(:disabled):active {
    transform: scale(0.985);
    border-color: var(--quiz-accent, var(--q-secondary));
}

.quiz-answer-letter {
    flex: none;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--quiz-accent, var(--q-secondary));
    color: white;
    font-weight: 700;
    font-size: 1.05rem;
}

.quiz-answer-text {
    flex: 1;
}

.quiz-answer-mark {
    flex: none;
    margin-left: auto;
}

/* vrai / faux : deux grandes tuiles côte à côte */
.quiz-answers--tf {
    flex-direction: row;
}

.quiz-answers--tf .quiz-answer-btn {
    flex: 1;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    min-height: 110px;
    font-size: 1.35rem;
    font-weight: 700;
    text-align: center;
}

/* états après réponse */
.quiz-answer-btn--correct {
    background: #e6f7eb !important;
    border-color: #21ba45 !important;
    color: #157f30 !important;
}

.quiz-answer-btn--correct .quiz-answer-letter {
    background: #21ba45;
}

.quiz-answer-btn--correct .quiz-answer-mark {
    color: #21ba45;
}

.quiz-answer-btn--wrong {
    background: #fdeaea !important;
    border-color: #c10015 !important;
    color: #a00012 !important;
}

.quiz-answer-btn--wrong .quiz-answer-letter {
    background: #c10015;
}

.quiz-answer-btn--wrong .quiz-answer-mark {
    color: #c10015;
}

.quiz-answer-btn--muted {
    opacity: 0.45;
}

.quiz-answer-btn:disabled {
    cursor: default;
}

/* ---- feedback + suivant ---- */
.quiz-next {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 14px;
    margin-top: 20px;
}

.quiz-feedback {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    font-size: 1.15rem;
}

.quiz-feedback--good {
    color: #21ba45;
}

.quiz-feedback--bad {
    color: #c10015;
}

.quiz-accent-btn {
    background: var(--quiz-accent, var(--q-secondary)) !important;
    color: white !important;
}

.quiz-big-btn {
    min-height: 56px;
    padding: 0 26px;
    font-size: 1.1rem;
    font-weight: 600;
}

/* ---- écran final ---- */
.quiz-end {
    text-align: center;
    padding-bottom: clamp(24px, 4vw, 40px);
}

.quiz-title {
    margin: 0 0 18px;
    font-size: clamp(1.3rem, 2.4vw, 1.7rem);
    color: #1a1a1a;
}

.quiz-score-circle {
    margin: 8px 0 14px;
}

.quiz-circular {
    color: var(--quiz-accent, var(--q-secondary));
}

.quiz-score {
    font-size: 2.4rem;
    font-weight: 700;
    color: #1a1a1a;
}

.quiz-score small {
    font-size: 1.3rem;
    color: #888;
    font-weight: 400;
}

.quiz-score-label {
    color: #555;
    font-size: 1.2rem;
    margin-bottom: 24px;
}

.quiz-open {
    margin-bottom: 8px;
}

.quiz-open-input {
    font-size: 1.1rem;
}

.quiz-thanks-icon {
    color: #21ba45;
    margin: 8px 0 6px;
}

.quiz-answer-btn--chosen {
    background: #eef3ff !important;
    border-color: var(--quiz-accent, var(--q-secondary)) !important;
}
</style>
